import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { Video } from 'lucide-react';

const HeroEditor = () => {
    const [content, setContent] = useState({
        title: '',
        subtitle: '',
        ctaText: '',
        videoUrl: '',
        overlayOpacity: 0.5,
    });
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [videoFile, setVideoFile] = useState(null);

    useEffect(() => {
        const fetchHero = async () => {
            try {
                const { data } = await api.get('/sections/Hero');
                if (data && data.content) {
                    const contentMap = {};
                    if (Array.isArray(data.content)) {
                        data.content.forEach(item => {
                            contentMap[item.key] = item.value;
                        });
                        setContent(prev => ({ ...prev, ...contentMap }));
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error(error);
                setLoading(false);
            }
        };

        fetchHero();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setContent((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setVideoFile(file);
        }
    };

    const handleSave = async () => {
        try {
            let currentVideoUrl = content.videoUrl;

            // Upload video if selected
            if (videoFile) {
                const formData = new FormData();
                formData.append('file', videoFile);
                const { data } = await api.post('/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                currentVideoUrl = data.path;
            }

            const contentArray = [
                { key: 'title', type: 'text', value: content.title },
                { key: 'subtitle', type: 'text', value: content.subtitle },
                { key: 'ctaText', type: 'text', value: content.ctaText },
                { key: 'videoUrl', type: 'video', value: currentVideoUrl },
                { key: 'overlayOpacity', type: 'text', value: content.overlayOpacity },
            ];

            await api.put('/sections/Hero', { content: contentArray });

            setContent(prev => ({ ...prev, videoUrl: currentVideoUrl }));
            setVideoFile(null); // Clear file input

            setMessage('Hero section updated successfully!');
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            setMessage('Error updating section');
            console.error(error);
        }
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="max-w-4xl mx-auto bg-white p-8 rounded shadow text-gray-900">
            <h2 className="text-2xl font-bold mb-6">Edit Hero Section</h2>

            {message && (
                <div className={cn("p-4 mb-4 rounded", message.includes("Error") ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700")}>
                    {message}
                </div>
            )}

            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Hero Title</label>
                    <input
                        type="text"
                        name="title"
                        value={content.title}
                        onChange={handleChange}
                        className="w-full mt-1 p-2 border rounded bg-white text-gray-900"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Subtitle</label>
                    <input
                        type="text"
                        name="subtitle"
                        value={content.subtitle}
                        onChange={handleChange}
                        className="w-full mt-1 p-2 border rounded bg-white text-gray-900"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">CTA Text</label>
                    <input
                        type="text"
                        name="ctaText"
                        value={content.ctaText}
                        onChange={handleChange}
                        className="w-full mt-1 p-2 border rounded bg-white text-gray-900"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Background Video</label>
                    <div className="mt-1 flex flex-col space-y-2">
                        {content.videoUrl && (
                            <div className="bg-gray-100 p-2 rounded mb-2">
                                <p className="text-sm font-mono text-gray-600 truncate mb-1">Current: {content.videoUrl}</p>
                                <video src={content.videoUrl} className="h-32 rounded bg-black" controls />
                            </div>
                        )}
                        <input
                            type="file"
                            accept="video/*"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-500
                                file:mr-4 file:py-2 file:px-4
                                file:rounded-full file:border-0
                                file:text-sm file:font-semibold
                                file:bg-blue-50 file:text-blue-700
                                hover:file:bg-blue-100"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Overlay Opacity</label>
                    <input
                        type="range"
                        name="overlayOpacity"
                        min="0"
                        max="1"
                        step="0.1"
                        value={content.overlayOpacity}
                        onChange={handleChange}
                        className="w-full mt-1"
                    />
                    <span className="text-sm text-gray-600">{content.overlayOpacity}</span>
                </div>

                <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800"
                >
                    Save Changes
                </button>
            </div>
        </div>
    );
};

export default HeroEditor;
